import {
    useRef,
    useState
} from 'react';

import { SimpleGrid } from '@mantine/core';

import { useBillingProviders } from '../hooks/useBillingProviders.ts';
import { useBillingProvidersSelection } from '../hooks/useBillingProvidersSelection.ts';
import { useTableColumns } from '../hooks/useTableColumns.tsx';
import { FormDrawer } from '../drawers/FormDrawer.tsx';
import { update } from '../services/BillingProvidersService.ts';

import {
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import { DataTableTokenPagination } from "../../../components/DataTableTokenPagination.tsx";
import { BillingProvider } from '../types';
import { useQueryClient } from '@tanstack/react-query';

export const RecordsTable = () => {
    const {selectedRecords, setSelectedRecords} = useBillingProvidersSelection();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<BillingProvider | null>(null);
    const queryClient = useQueryClient();

    const handleRecordClick = (record: BillingProvider) => {
        setSelectedRecord(record);
        setDrawerOpened(true);
    };

    const { columns } = useTableColumns(handleRecordClick);

    const {
        items,
        totalCount,
        loading,
        nextToken,
        fetchRecords,
        handleFilterByKeyword
    } = useBillingProviders();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const handleDrawerSubmit = async (values: BillingProvider) => {
        if (selectedRecord?.id) {
            const { createdByUsername: _createdByUsername, createdBy: _createdBy, ...rest } = values;
            await update(selectedRecord.id, rest as BillingProvider);
            queryClient.invalidateQueries({ queryKey: ['billing-providers'] });
        }
    };

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword} />

            <MantineDataTable
                columns={columns}
                records={items}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            <DataTableTokenPagination totalCount={totalCount.toString()} nextToken={nextToken} handleLoadMore={fetchRecords} />

            {drawerOpened && selectedRecord && (
                <FormDrawer
                    record={selectedRecord}
                    handleSubmit={handleDrawerSubmit}
                    opened={drawerOpened}
                    onClose={() => {
                        setDrawerOpened(false);
                        setSelectedRecord(null);
                    }}
                />
            )}
        </SimpleGrid>
    );
};
